/**
 * Mould Compensation Tool
 * Inverse CTE calculator: given desired final part dimensions,
 * calculates the pre-compensated mould CAD size so that thermal
 * expansion at cure temperature produces the correct shape.
 *
 * Supports simple X/Y/Z dimensions, full STL/STEP compensation with download,
 * and multi-angle compensation driven by the robot toolpath (SRC + STEP).
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  integrateCTE, rotateCTETensor, buildAngleToEuler,
  computeBaseRef, getDispMagnitude,
  exportBinarySTL, meshExtents,
} from './cte-utils';
import { STLViewer, ColorBar, BuildAngleGizmo, useCadModelUpload } from './cte-shared';
import CTEAIPanel from './CTEAIPanel';
import MultiAngleCompensation from './MultiAngleCompensation';
import { authFetch } from '../../utils/api';

// ─── SIMPLE DIMENSION COMPENSATION ───────────────────────────────────────────
function SimpleCompensation({ materials }) {
  const mouldMaterials = materials.filter(m => m.category === 'mould');
  const [selId, setSelId] = useState(mouldMaterials[0]?.id || materials[0]?.id || '');
  const [dims, setDims] = useState({ x: 1000, y: 500, z: 10 });
  const [temps, setTemps] = useState({ t1: 23, t2: 120 });
  const [buildAngle, setBuildAngle] = useState(0);
  const [buildAzimuth, setBuildAzimuth] = useState(0);
  const orient = useMemo(() => buildAngleToEuler(buildAngle, buildAzimuth), [buildAngle, buildAzimuth]);
  const [result, setResult] = useState(null);
  const [compAIData, setCompAIData] = useState(null);

  const mat = materials.find(m => m.id === selId);

  const handleBuildAngleChange = useCallback((angle, azimuth) => {
    setBuildAngle(angle);
    setBuildAzimuth(azimuth);
    setResult(null);
  }, []);

  const calculate = () => {
    if (!mat) return;
    const axStrain = integrateCTE(mat, 'ax', temps.t1, temps.t2) * 1e-6;
    const ayStrain = integrateCTE(mat, 'ay', temps.t1, temps.t2) * 1e-6;
    const azStrain = integrateCTE(mat, 'az', temps.t1, temps.t2) * 1e-6;
    const tensor = rotateCTETensor(axStrain, ayStrain, azStrain, orient.rx, orient.ry, orient.rz);

    const strainXX = tensor[0][0];
    const strainYY = tensor[1][1];
    const strainZZ = tensor[2][2];

    const compX = dims.x / (1 + strainXX);
    const compY = dims.y / (1 + strainYY);
    const compZ = dims.z / (1 + strainZZ);

    const r = {
      desired: { x: dims.x, y: dims.y, z: dims.z },
      compensated: { x: compX, y: compY, z: compZ },
      delta: { x: compX - dims.x, y: compY - dims.y, z: compZ - dims.z },
      strain: { x: strainXX * 1e6, y: strainYY * 1e6, z: strainZZ * 1e6 },
      shearCoupling: buildAngle > 0,
      matName: mat.name,
      deltaT: temps.t2 - temps.t1,
    };
    setResult(r);

    setCompAIData({
      type: 'compensation',
      materialName: mat.name,
      cureTemp: mat.cureTemp,
      t1: temps.t1,
      t2: temps.t2,
      deltaT: r.deltaT,
      buildAngle,
      desiredX: dims.x, desiredY: dims.y, desiredZ: dims.z,
      compX: compX.toFixed(4), compY: compY.toFixed(4), compZ: compZ.toFixed(4),
      deltaX: r.delta.x.toFixed(4), deltaY: r.delta.y.toFixed(4), deltaZ: r.delta.z.toFixed(4),
      strainX: r.strain.x.toFixed(2), strainY: r.strain.y.toFixed(2), strainZ: r.strain.z.toFixed(2),
      shearCoupling: buildAngle > 0,
    });
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <div className="cte-info-box" style={{ marginBottom: 16 }}>
        Enter the <strong>desired final part dimensions</strong> (the size you want after curing).
        The tool calculates the <strong>compensated mould CAD dimensions</strong> — the size to model in CAD
        so that when the mould thermally expands at cure temperature, the cavity matches the desired shape.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="cte-card">
          <div className="cte-card-header">Material & Temperatures</div>
          <div className="cte-card-body">
            <div className="cte-form-group" style={{ marginBottom: 12 }}>
              <label>Mould Material</label>
              <select value={selId} onChange={e => { setSelId(e.target.value); setResult(null); }}>
                <optgroup label="Mould / Tool Materials">
                  {materials.filter(m => m.category === 'mould').map(m => (
                    <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
                  ))}
                </optgroup>
                <optgroup label="Layup / Part Materials">
                  {materials.filter(m => m.category === 'layup').map(m => (
                    <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
                  ))}
                </optgroup>
              </select>
            </div>
            {mat?.cureTemp && (
              <div className="cte-info-box" style={{ marginBottom: 12, fontSize: 'var(--rf-font-size-xs)' }}>
                Material Tg: {mat.cureTemp} deg C | Post-Tg scale x{mat.postCureScale}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div className="cte-form-group">
                <label>T1 Room Temp (deg C)</label>
                <input type="number" value={temps.t1} onChange={e => { setTemps(t => ({ ...t, t1: +e.target.value })); setResult(null); }} />
              </div>
              <div className="cte-form-group">
                <label>T2 Cure Temp (deg C)</label>
                <input type="number" value={temps.t2} onChange={e => { setTemps(t => ({ ...t, t2: +e.target.value })); setResult(null); }} />
              </div>
            </div>
            <div className="cte-section-label" style={{ marginBottom: 8 }}>Desired Part Dimensions (mm)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {[['x', 'X Length'], ['y', 'Y Width'], ['z', 'Z Thickness']].map(([k, label]) => (
                <div className="cte-form-group" key={k}>
                  <label>{label}</label>
                  <input type="number" value={dims[k]} onChange={e => { setDims(d => ({ ...d, [k]: +e.target.value })); setResult(null); }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="cte-card">
          <div className="cte-card-header">AM Print Orientation</div>
          <div className="cte-card-body">
            <BuildAngleGizmo buildAngle={buildAngle} buildAzimuth={buildAzimuth} onAngleChange={handleBuildAngleChange} size={dims} />
            <div className="cte-angle-controls">
              <div className="cte-form-group" style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Build Angle</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={0} max={90} value={buildAngle}
                      onChange={e => handleBuildAngleChange(Math.max(0, Math.min(90, +e.target.value || 0)), buildAzimuth)}
                      style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                    <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                  </span>
                </label>
                <input type="range" min="0" max="90" step="1" value={buildAngle}
                  onChange={e => handleBuildAngleChange(+e.target.value, buildAzimuth)} style={{ width: '100%' }} />
              </div>
              <div className="cte-form-group" style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Rotation</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={0} max={359} value={buildAzimuth}
                      onChange={e => handleBuildAngleChange(buildAngle, Math.max(0, Math.min(359, +e.target.value || 0)))}
                      style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                    <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                  </span>
                </label>
                <input type="range" min="0" max="359" step="1" value={buildAzimuth}
                  onChange={e => handleBuildAngleChange(buildAngle, +e.target.value)} style={{ width: '100%' }} />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={() => handleBuildAngleChange(0, 0)}>
                Reset to Flat Print
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button className="cte-btn cte-btn-primary" onClick={calculate} disabled={!mat}>
          Calculate Compensated Dimensions
        </button>
      </div>

      {result && (
        <>
          <div className="cte-card">
            <div className="cte-card-header">
              Compensated Mould Dimensions
              {result.shearCoupling && <span className="cte-badge warn">Shear coupling active</span>}
            </div>
            <div className="cte-card-body">
              <div style={{ marginBottom: 16, fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-secondary)' }}>
                {result.matName} | Delta T = {result.deltaT} deg C
              </div>
              <div className="cte-axis-grid">
                {['x', 'y', 'z'].map((axis) => (
                  <div key={axis} className="cte-axis-card">
                    <div className="cte-axis-label">AXIS {axis.toUpperCase()}</div>
                    <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginBottom: 8 }}>
                      Desired: {result.desired[axis].toFixed(2)} mm
                    </div>
                    <div className="cte-axis-value">
                      {result.compensated[axis].toFixed(4)}
                    </div>
                    <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginTop: 3 }}>mm (CAD size)</div>
                    <div className="cte-axis-details">
                      Delta: {result.delta[axis] >= 0 ? '+' : ''}{result.delta[axis].toFixed(4)} mm<br />
                      Strain: {result.strain[axis].toFixed(2)} um/m
                    </div>
                  </div>
                ))}
              </div>
              {result.shearCoupling && (
                <div className="cte-warn-box">
                  <span>Note:</span>
                  <span>
                    Non-zero print orientation introduces off-diagonal shear coupling.
                    Simple per-axis compensation is approximate — use the STL/STEP compensation below for full 3D accuracy.
                  </span>
                </div>
              )}
            </div>
          </div>

          {compAIData && (
            <CTEAIPanel analysisData={compAIData} label="Compensation — AI Insight" />
          )}
        </>
      )}
    </div>
  );
}

// ─── STL / STEP COMPENSATION ──────────────────────────────────────────────────
function ModelCompensation({ materials }) {
  const mouldMaterials = materials.filter(m => m.category === 'mould');
  const [selId, setSelId] = useState(mouldMaterials[0]?.id || materials[0]?.id || '');
  const [temps, setTemps] = useState({ t1: 23, t2: 120 });
  const [buildAngle, setBuildAngle] = useState(0);
  const [buildAzimuth, setBuildAzimuth] = useState(0);
  const orient = useMemo(() => buildAngleToEuler(buildAngle, buildAzimuth), [buildAngle, buildAzimuth]);

  const { model, setModel, loadFile } = useCadModelUpload();
  const positions = model.positions;
  const fileName = model.fileName;
  const fileIsStep = model.isStep;
  const rawFileBytes = model.rawBytes;
  const error = model.error;
  const meshWarning = model.warning;
  const loading = model.loading;
  const plateSize = useMemo(() => meshExtents(positions), [positions]);
  const setError = (message) => setModel((m) => ({ ...m, error: message }));
  const [stepExporting, setStepExporting] = useState(false);

  const [compResult, setCompResult] = useState(null);
  const [modelAIData, setModelAIData] = useState(null);
  const [showOriginal, setShowOriginal] = useState(true);
  const [defScale, setDefScale] = useState(1);

  const fileRef = useRef();
  const mat = materials.find(m => m.id === selId);

  const handleBuildAngleChange = useCallback((angle, azimuth) => {
    setBuildAngle(angle);
    setBuildAzimuth(azimuth);
    setCompResult(null);
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setCompResult(null);
    await loadFile(file);
  };

  const runCompensation = () => {
    if (!mat || !positions) return;

    const axStrain = integrateCTE(mat, 'ax', temps.t1, temps.t2) * 1e-6;
    const ayStrain = integrateCTE(mat, 'ay', temps.t1, temps.t2) * 1e-6;
    const azStrain = integrateCTE(mat, 'az', temps.t1, temps.t2) * 1e-6;
    const tensor = rotateCTETensor(axStrain, ayStrain, azStrain, orient.rx, orient.ry, orient.rz);
    const [e00, e01, e02, e10, e11, e12, e20, e21, e22] = tensor.flat();

    const nv = positions.length / 3;
    const base = computeBaseRef(positions);
    const compensated = new Float32Array(positions.length);

    let maxCompMM = 0;
    let totalCompMM = 0;

    for (let i = 0; i < nv; i++) {
      const px = positions[i * 3] - base.x;
      const py = positions[i * 3 + 1] - base.y;
      const pz = positions[i * 3 + 2] - base.z;

      const dx = e00 * px + e01 * py + e02 * pz;
      const dy = e10 * px + e11 * py + e12 * pz;
      const dz = e20 * px + e21 * py + e22 * pz;

      compensated[i * 3]     = positions[i * 3]     - dx;
      compensated[i * 3 + 1] = positions[i * 3 + 1] - dy;
      compensated[i * 3 + 2] = positions[i * 3 + 2] - dz;

      const mag = getDispMagnitude(dx, dy, dz);
      if (mag > maxCompMM) maxCompMM = mag;
      totalCompMM += mag;
    }

    const negTensor = tensor.map(row => row.map(v => -v));

    const cr = {
      compensatedPositions: compensated,
      strainTensor: negTensor,
      tensor,
      base,
      maxCompMM,
      avgCompMM: totalCompMM / nv,
      nTriangles: nv / 3,
      deltaT: temps.t2 - temps.t1,
      matName: mat.name,
      strainDiag: {
        x: tensor[0][0] * 1e6,
        y: tensor[1][1] * 1e6,
        z: tensor[2][2] * 1e6,
      },
    };
    setCompResult(cr);

    setModelAIData({
      type: 'compensation',
      materialName: mat.name,
      cureTemp: mat.cureTemp,
      t1: temps.t1,
      t2: temps.t2,
      deltaT: cr.deltaT,
      buildAngle,
      maxCompMM: cr.maxCompMM.toFixed(4),
      avgCompMM: cr.avgCompMM.toFixed(4),
      nTriangles: cr.nTriangles,
      strainX: cr.strainDiag.x.toFixed(2),
      strainY: cr.strainDiag.y.toFixed(2),
      strainZ: cr.strainDiag.z.toFixed(2),
      shearCoupling: buildAngle > 0,
    });
  };

  const DOWNLOAD_DISCLAIMER =
    'DISCLAIMER\n\n' +
    'Rapid Fusion accepts no responsibility for the accuracy of these results. ' +
    'This tool is provided as a computational aid only.\n\n' +
    'A qualified engineer must review and verify all compensated geometry before manufacturing.\n\n' +
    'Do you wish to proceed with the download?';

  const downloadSTL = () => {
    if (!compResult) return;
    if (!window.confirm(DOWNLOAD_DISCLAIMER)) return;
    const buffer = exportBinarySTL(compResult.compensatedPositions);
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const baseName = fileName ? fileName.replace(/\.[^.]+$/, '') : 'mould';
    a.href = url;
    a.download = `${baseName}_compensated.stl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSTEP = async () => {
    if (!compResult || !rawFileBytes || !fileIsStep) return;
    if (!window.confirm(DOWNLOAD_DISCLAIMER)) return;
    setStepExporting(true);
    setError(null);

    try {
      const tensor = compResult.tensor;
      const base = compResult.base;

      const matrix = [
        [1 - tensor[0][0], -tensor[0][1], -tensor[0][2]],
        [-tensor[1][0], 1 - tensor[1][1], -tensor[1][2]],
        [-tensor[2][0], -tensor[2][1], 1 - tensor[2][2]],
      ];

      const translation = [
        tensor[0][0] * base.x + tensor[0][1] * base.y + tensor[0][2] * base.z,
        tensor[1][0] * base.x + tensor[1][1] * base.y + tensor[1][2] * base.z,
        tensor[2][0] * base.x + tensor[2][1] * base.y + tensor[2][2] * base.z,
      ];

      const formData = new FormData();
      const blob = new Blob([rawFileBytes], { type: 'application/step' });
      formData.append('stepFile', blob, fileName);
      formData.append('matrix', JSON.stringify(matrix));
      formData.append('translation', JSON.stringify(translation));

      const submitRes = await authFetch('/api/cte/compensate-step', {
        method: 'POST',
        body: formData,
      });

      if (!submitRes.ok) {
        const errData = await submitRes.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${submitRes.status}`);
      }

      const { jobId } = await submitRes.json();

      const poll = async () => {
        while (true) {
          await new Promise((r) => setTimeout(r, 2000));
          const statusRes = await authFetch(`/api/cte/compensate-step/status/${jobId}`);
          if (!statusRes.ok) throw new Error('Lost connection to export job');
          const { status, error: jobError } = await statusRes.json();
          if (status === 'error') throw new Error(jobError || 'STEP processing failed on server');
          if (status === 'done') return;
        }
      };
      await poll();

      const dlRes = await authFetch(`/api/cte/compensate-step/download/${jobId}`);
      if (!dlRes.ok) throw new Error('Download failed — file may have expired');

      const resultBlob = await dlRes.blob();
      const url = URL.createObjectURL(resultBlob);
      const a = document.createElement('a');
      const baseName = fileName ? fileName.replace(/\.[^.]+$/, '') : 'mould';
      a.href = url;
      a.download = `${baseName}_compensated.step`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('STEP export failed: ' + err.message);
    } finally {
      setStepExporting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000 }}>
      {loading && (
        <div className="cte-loading-overlay">
          <div className="cte-loading-modal">
            <div className="cte-loading-spinner" />
            <div className="cte-loading-title">Parsing CAD File</div>
            <div className="cte-loading-sub">{fileName || 'Loading...'}</div>
            <div className="cte-loading-hint">STEP files require the geometry engine to load. This can take a moment.</div>
          </div>
        </div>
      )}
      <div className="cte-info-box" style={{ marginBottom: 16 }}>
        Upload an <strong>STL, OBJ, or STEP</strong> file of the desired final part shape.
        The tool generates a compensated mould geometry — pre-shrunk so that when the mould
        thermally expands at cure temperature, it matches the target geometry.
        {' '}<strong>STEP files</strong> can be exported back as compensated STEP with exact surface quality preserved.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="cte-card">
          <div className="cte-card-header">Material & Temperatures</div>
          <div className="cte-card-body">
            <div className="cte-form-group" style={{ marginBottom: 12 }}>
              <label>Mould Material</label>
              <select value={selId} onChange={e => { setSelId(e.target.value); setCompResult(null); }}>
                <optgroup label="Mould / Tool Materials">
                  {materials.filter(m => m.category === 'mould').map(m => (
                    <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
                  ))}
                </optgroup>
                <optgroup label="Layup / Part Materials">
                  {materials.filter(m => m.category === 'layup').map(m => (
                    <option key={m.id} value={m.id}>{m.name} (v{m.version})</option>
                  ))}
                </optgroup>
              </select>
            </div>
            {mat?.cureTemp && (
              <div className="cte-info-box" style={{ marginBottom: 12, fontSize: 'var(--rf-font-size-xs)' }}>
                Material Tg: {mat.cureTemp} deg C | Post-Tg scale x{mat.postCureScale}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="cte-form-group">
                <label>T1 Room Temp (deg C)</label>
                <input type="number" value={temps.t1} onChange={e => { setTemps(t => ({ ...t, t1: +e.target.value })); setCompResult(null); }} />
              </div>
              <div className="cte-form-group">
                <label>T2 Cure Temp (deg C)</label>
                <input type="number" value={temps.t2} onChange={e => { setTemps(t => ({ ...t, t2: +e.target.value })); setCompResult(null); }} />
              </div>
            </div>
          </div>
        </div>

        <div className="cte-card">
          <div className="cte-card-header">
            <span>AM Print Orientation</span>
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)' }}>Click or drag the polar grid</span>
          </div>
          <div className="cte-card-body">
            <BuildAngleGizmo buildAngle={buildAngle} buildAzimuth={buildAzimuth} onAngleChange={handleBuildAngleChange} size={plateSize} />
            <div className="cte-angle-controls">
              <div className="cte-form-group" style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Build Angle</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={0} max={90} value={buildAngle}
                      onChange={e => handleBuildAngleChange(Math.max(0, Math.min(90, +e.target.value || 0)), buildAzimuth)}
                      style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                    <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                  </span>
                </label>
                <input type="range" min="0" max="90" step="1" value={buildAngle}
                  onChange={e => handleBuildAngleChange(+e.target.value, buildAzimuth)} style={{ width: '100%' }} />
              </div>
              <div className="cte-form-group" style={{ flex: 1 }}>
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Rotation</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="number" min={0} max={359} value={buildAzimuth}
                      onChange={e => handleBuildAngleChange(buildAngle, Math.max(0, Math.min(359, +e.target.value || 0)))}
                      style={{ width: 52, textAlign: 'right', padding: '2px 4px', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', background: 'var(--rf-bg-tertiary)', border: '1px solid var(--rf-border-color)', borderRadius: 4, fontWeight: 600 }} />
                    <span style={{ color: 'var(--rf-primary)', fontSize: 'var(--rf-font-size-xs)' }}>deg</span>
                  </span>
                </label>
                <input type="range" min="0" max="359" step="1" value={buildAzimuth}
                  onChange={e => handleBuildAngleChange(buildAngle, +e.target.value)} style={{ width: '100%' }} />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <button className="cte-btn cte-btn-ghost cte-btn-sm" onClick={() => handleBuildAngleChange(0, 0)}>
                Reset to Flat Print
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="cte-card" style={{ marginBottom: 16 }}>
        <div className="cte-card-header">3D Model Input</div>
        <div className="cte-card-body">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input ref={fileRef} type="file" accept=".stl,.obj,.step,.stp" onChange={handleFile} style={{ display: 'none' }} />
            <button className="cte-btn cte-btn-primary" onClick={() => fileRef.current.click()} disabled={loading}>
              Upload STL / OBJ / STEP
            </button>
            {fileName && (
              <span style={{ fontSize: 'var(--rf-font-size-sm)', color: 'var(--rf-text-secondary)' }}>
                {fileName}
                {fileIsStep && <span style={{ color: 'var(--rf-info)', marginLeft: 8 }}>STEP</span>}
                {positions && <span style={{ color: 'var(--rf-success)', marginLeft: 10 }}>
                  {(positions.length / 9).toLocaleString()} triangles
                </span>}
              </span>
            )}
            {positions && !loading && (
              <button className="cte-btn cte-btn-primary" style={{ marginLeft: 'auto' }} onClick={runCompensation}>
                Generate Compensated Mould
              </button>
            )}
          </div>
          {error && <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)', marginTop: 10 }}>{error}</div>}
          {meshWarning && (
            <div className="cte-warn-box" style={{ marginTop: 10 }}>
              <span>Note:</span>
              <span>{meshWarning}</span>
            </div>
          )}
          {!positions && !loading && (
            <div className="cte-upload-area">
              <div className="cte-upload-icon">
                <i className="fas fa-cube"></i>
              </div>
              Upload an STL, OBJ, or STEP file of the desired final part shape.<br />
              The tool will generate a compensated mould geometry for download.<br />
              The file stays loaded when you change tabs.
            </div>
          )}
        </div>
      </div>

      {positions && !compResult && !loading && (
        <div className="cte-card" style={{ marginBottom: 16 }}>
          <div className="cte-card-header">
            <span>Model Preview — Verify Build Direction</span>
            <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)' }}>Purple arrow = BUILD direction</span>
          </div>
          <div className="cte-card-body">
            <STLViewer
              positions={positions}
              strainTensor={[[0,0,0],[0,0,0],[0,0,0]]}
              maxDisp={0}
              showOriginal={false}
              orient={orient}
            />
          </div>
        </div>
      )}

      {compResult && positions && (
        <>
          <div className="cte-stats-grid">
            {[
              { label: 'Max Compensation', value: compResult.maxCompMM.toFixed(4), unit: 'mm' },
              { label: 'Avg Compensation', value: compResult.avgCompMM.toFixed(4), unit: 'mm' },
              { label: 'Triangles', value: compResult.nTriangles.toLocaleString(), unit: '' },
              { label: 'Delta T', value: compResult.deltaT.toFixed(1), unit: 'deg C' },
            ].map(({ label, value, unit }) => (
              <div key={label} className="cte-stat-box">
                <div className="cte-stat-label">{label}</div>
                <div className="cte-stat-value">{value}</div>
                <div className="cte-stat-unit">{unit}</div>
              </div>
            ))}
          </div>

          <div className="cte-card" style={{ marginBottom: 16 }}>
            <div className="cte-card-header">Per-Axis Strain</div>
            <div className="cte-card-body">
              <div className="cte-axis-grid">
                {[
                  { axis: 'X', strain: compResult.strainDiag.x },
                  { axis: 'Y', strain: compResult.strainDiag.y },
                  { axis: 'Z', strain: compResult.strainDiag.z },
                ].map(({ axis, strain }) => (
                  <div key={axis} className="cte-axis-card">
                    <div className="cte-axis-label">AXIS {axis}</div>
                    <div className="cte-axis-value">{strain.toFixed(2)}</div>
                    <div style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', marginTop: 3 }}>um/m (global frame)</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="cte-card">
            <div className="cte-card-header">
              <span>Compensated Mould Preview</span>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', textTransform: 'none', fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-secondary)' }}>
                  <input type="checkbox" checked={showOriginal} onChange={e => setShowOriginal(e.target.checked)}
                    style={{ width: 12, height: 12 }} />
                  Original wireframe
                </label>
                <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={downloadSTL}>
                  Download Compensated STL
                </button>
                {fileIsStep && rawFileBytes && (
                  <button className="cte-btn cte-btn-primary cte-btn-sm" onClick={downloadSTEP} disabled={stepExporting}>
                    {stepExporting ? 'Exporting STEP...' : 'Download Compensated STEP'}
                  </button>
                )}
              </div>
            </div>
            <div className="cte-card-body">
              {error && (
                <div style={{ color: 'var(--rf-danger)', fontSize: 'var(--rf-font-size-sm)', marginBottom: 12, padding: '8px 12px', background: 'rgba(var(--rf-danger-rgb), 0.1)', borderRadius: 6 }}>
                  {error}
                </div>
              )}
              <div className="cte-info-box" style={{ marginBottom: 12 }}>
                The coloured mesh shows the compensated mould (smaller than desired).
                The wireframe overlay is the original desired shape for comparison.
                At cure temperature the compensated mould will expand to match the desired geometry.
              </div>
              <STLViewer
                positions={positions}
                strainTensor={compResult.strainTensor}
                maxDisp={compResult.maxCompMM}
                showOriginal={showOriginal}
                orient={orient}
                defScale={defScale}
              />
              <ColorBar maxVal={compResult.maxCompMM} unit="mm" />
              <div className="cte-timeline-controls" style={{ marginTop: 8 }}>
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-text-muted)', whiteSpace: 'nowrap' }}>Visual scale (display only)</span>
                <input type="range" className="cte-timeline-slider" min={1} max={10} value={defScale}
                  onChange={e => setDefScale(+e.target.value)} />
                <span style={{ fontSize: 'var(--rf-font-size-xs)', color: 'var(--rf-primary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {defScale === 1 ? 'true scale' : `${defScale}× shown larger`}
                </span>
              </div>
            </div>
          </div>

          {modelAIData && (
            <CTEAIPanel analysisData={modelAIData} label="3D Compensation — AI Insight" />
          )}
        </>
      )}
    </div>
  );
}

// ─── MAIN MOULD COMPENSATION TAB ─────────────────────────────────────────────
export default function MouldCompensation({ materials }) {
  const [mode, setMode] = useState('simple');

  return (
    <div>
      <div className="cte-comp-mode-toggle" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          className={`cte-btn ${mode === 'simple' ? 'cte-btn-primary' : 'cte-btn-ghost'}`}
          onClick={() => setMode('simple')}
        >
          Simple Dimensions
        </button>
        <button
          className={`cte-btn ${mode === 'model' ? 'cte-btn-primary' : 'cte-btn-ghost'}`}
          onClick={() => setMode('model')}
        >
          STL / STEP Compensation
        </button>
        <button
          className={`cte-btn ${mode === 'multiangle' ? 'cte-btn-primary' : 'cte-btn-ghost'}`}
          onClick={() => setMode('multiangle')}
          title="Robot program + part CAD: scaling that follows the angle each region was printed at" 
        >
          SRC + STEP Multi-Angle
        </button>
      </div>

      {mode === 'simple' && <SimpleCompensation materials={materials} />}
      {mode === 'model' && <ModelCompensation materials={materials} />}
      {mode === 'multiangle' && <MultiAngleCompensation materials={materials} />}
    </div>
  );
}
